import type { IDatadogLogger } from "./DatadogLoggerTypes";

/**
 * Shared Datadog config helpers for the profile and RNVJ viewjob **web**
 * bundles. Extracted from the two previously-identical copies in
 * `web/profile/src/datadog/datadogConfig.ts` and
 * `web/viewjob/src/datadog/datadogConfig.ts` (RN-5721).
 *
 * Only the *stateless* pieces live here — the config shape + guard, URL/PII
 * scrubbing (including a base `beforeSend` scrubber), `Error` reconstruction,
 * and the logs and RUM logger methods. Init flow and host-context sync stay
 * per-surface because they diverge by design: profile self-initializes at
 * module load and ships `@datadog/browser-rum`; viewjob is dependency-injected
 * (for the strict/non-strict bundle split) and stubs RUM; onboarding reuses a
 * host-started SDK on jso-ui.
 *
 * This module is intentionally free of any `@datadog/*` import: it lives in the
 * `@indeed/jobsearch-react-native-bundle` package, which also builds for native,
 * so it must not pull in the web-only `@datadog/browser-logs` SDK. The one piece
 * that talks to the SDK — the `debug`/`info`/`warn`/`error` methods — receives
 * the logs `logger` by injection (`DatadogLogsSink`); each web surface passes
 * its own `datadogLogs.logger`, where `@datadog/browser-logs` is a declared dep.
 */

export type DatadogWebConfig = {
  /**
   * RUM-only. `applicationId` appears nowhere in `@datadog/browser-logs`; `datadogLogs.init()`
   * validates `clientToken` alone. Optional so a Logs-only surface can be configured without
   * provisioning a RUM application it will never use — see `hasRequiredLogsConfig`.
   */
  applicationId?: string;
  clientToken: string;
  site: string;
  service: string;
  version: string;
  env: string;
  sessionSampleRate: number;
  sessionReplaySampleRate?: number;
  trackResources?: boolean;
  trackLongTasks?: boolean;
  trackUserInteractions?: boolean;
  defaultPrivacyLevel?: "allow" | "mask" | "mask-user-input";
};

/** Everything Browser Logs needs. Deliberately excludes the RUM-only `applicationId`. */
export type RequiredDatadogLogsConfig = Pick<
  DatadogWebConfig,
  "clientToken" | "site" | "service" | "version" | "env" | "sessionSampleRate"
>;

/** Logs fields plus `applicationId`, for surfaces that also call `datadogRum.init()`. */
export type RequiredDatadogWebConfig = RequiredDatadogLogsConfig & {
  applicationId: string;
};

const TOKEN_SUFFIX_PATTERN = /token$/i;
const SENSITIVE_FIELD_NAMES = new Set([
  "email",
  "emailaddress",
  "phone",
  "phonenumber",
  "address",
  "password",
  "secret",
]);

/**
 * Guard for Logs-only surfaces (myjobs). Omits `applicationId`, which the Logs SDK neither reads
 * nor validates, so such a surface needs only a client token rather than a RUM application.
 */
export function hasRequiredLogsConfig(
  config?: Partial<DatadogWebConfig>,
): config is RequiredDatadogLogsConfig {
  return Boolean(
    config?.clientToken &&
    config.site &&
    config.service &&
    config.version &&
    config.env &&
    typeof config.sessionSampleRate === "number",
  );
}

/**
 * Guard for surfaces that initialize RUM as well as Logs (profile), and for viewjob, which
 * supplies an `applicationId` it does not use. Behaviour is unchanged from before the
 * Logs-only split.
 */
export function hasRequiredConfig(
  config?: Partial<DatadogWebConfig>,
): config is RequiredDatadogWebConfig {
  return Boolean(config?.applicationId) && hasRequiredLogsConfig(config);
}

export function sanitizeUrl(url?: string): string | undefined {
  if (!url) {
    return url;
  }

  try {
    const parsedUrl = new URL(url, globalThis.location?.origin);
    parsedUrl.search = "";
    parsedUrl.hash = "";
    return parsedUrl.toString();
  } catch {
    return String(url).split("?")[0]?.split("#")[0];
  }
}

function isSensitiveFieldName(fieldName: string): boolean {
  const normalizedFieldName = fieldName.replace(/[_-]/g, "").toLowerCase();
  return SENSITIVE_FIELD_NAMES.has(normalizedFieldName) || TOKEN_SUFFIX_PATTERN.test(fieldName);
}

export function scrubContext(value: unknown): unknown {
  if (!value || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(scrubContext);
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      isSensitiveFieldName(key) ? "[REDACTED]" : scrubContext(entry),
    ]),
  );
}

const RESIZE_OBSERVER_LOOP_PATTERN = /ResizeObserver loop/i;

/** The event fields `scrubDatadogEvent` reads; RUM and Logs events are structurally assignable. */
export type ScrubbableDatadogEvent = {
  view?: { url?: string; referrer?: string };
  resource?: { url?: string };
  /** RUM network errors carry the request URL on `error.resource`. */
  error?: { message?: string; resource?: { url?: string } };
  /** Logs network errors carry the request URL on `http`, not `resource`. */
  http?: { url?: string };
  context?: unknown;
  message?: string;
};

/** Browser-generated `ResizeObserver loop` warnings are benign noise, not app errors. */
export function isResizeObserverLoopEvent(event: ScrubbableDatadogEvent): boolean {
  return (
    RESIZE_OBSERVER_LOOP_PATTERN.test(event.error?.message ?? "") ||
    RESIZE_OBSERVER_LOOP_PATTERN.test(event.message ?? "")
  );
}

/**
 * Base `beforeSend` for RUM and Logs: drops `ResizeObserver loop` noise, strips query strings and
 * hashes from every request/page URL (view, resource, RUM network error, Logs `http`), and redacts
 * sensitive context fields. Returns `false` to drop the event. Surfaces with extra needs wrap it.
 */
export function scrubDatadogEvent(event: ScrubbableDatadogEvent): boolean {
  if (isResizeObserverLoopEvent(event)) {
    return false;
  }

  if (event.view) {
    event.view.url = sanitizeUrl(event.view.url);
    event.view.referrer = sanitizeUrl(event.view.referrer);
  }

  if (event.resource) {
    event.resource.url = sanitizeUrl(event.resource.url);
  }

  if (event.error?.resource) {
    event.error.resource.url = sanitizeUrl(event.error.resource.url);
  }

  // Indeed URLs can carry credentials in the query string (e.g. `ctk`, session tokens), and
  // `scrubContext` only matches context keys, so URLs must be sanitized here.
  if (event.http) {
    event.http.url = sanitizeUrl(event.http.url);
  }

  if (event.context) {
    event.context = scrubContext(event.context);
  }

  return true;
}

export function createError(
  message: string,
  errorKind?: string,
  errorMessage?: string,
  stacktrace?: string,
): Error {
  const error = new Error(errorMessage ?? message);

  if (errorKind) {
    error.name = errorKind;
  }

  if (stacktrace) {
    error.stack = stacktrace;
  }

  return error;
}

export function shouldAttachError(
  errorKind?: string,
  errorMessage?: string,
  stacktrace?: string,
): boolean {
  return Boolean(errorKind || errorMessage || stacktrace);
}

/**
 * Minimal structural view of `@datadog/browser-logs`'s `datadogLogs.logger`.
 * Declared locally (rather than importing the SDK type) so this module stays
 * `@datadog/*`-free; `datadogLogs.logger` is structurally assignable to it.
 */
export type DatadogLogsSink = {
  debug: (message: string, context?: object, error?: Error) => void;
  info: (message: string, context?: object, error?: Error) => void;
  warn: (message: string, context?: object, error?: Error) => void;
  error: (message: string, context?: object, error?: Error) => void;
};

/** The logs-only `IDatadogLogger` methods; RUM methods are supplied per-surface. */
type DatadogLogsMethods = Pick<IDatadogLogger, "debug" | "info" | "warn" | "error">;

/**
 * The `debug`/`info`/`warn`/`error` methods shared by both web loggers.
 * `logger` is the injected `datadogLogs.logger`; `toDatadogContext` applies each
 * surface's own context enrichment (profile imports `enrichContext` at module
 * scope; viewjob injects it via deps).
 */
export function createDatadogLogsMethods(
  logger: DatadogLogsSink,
  toDatadogContext: (context?: Record<string, unknown>) => object,
): DatadogLogsMethods {
  return {
    debug: (message: string, context?: Record<string, unknown>): Promise<void> => {
      logger.debug(message, toDatadogContext(context));
      return Promise.resolve();
    },

    info: (message: string, context?: Record<string, unknown>): Promise<void> => {
      logger.info(message, toDatadogContext(context));
      return Promise.resolve();
    },

    warn: (
      message: string,
      context?: Record<string, unknown>,
      errorKind?: string,
      errorMessage?: string,
      stacktrace?: string,
    ): Promise<void> => {
      const datadogContext = toDatadogContext(context);
      if (shouldAttachError(errorKind, errorMessage, stacktrace)) {
        logger.warn(
          message,
          datadogContext,
          createError(message, errorKind, errorMessage, stacktrace),
        );
      } else {
        logger.warn(message, datadogContext);
      }
      return Promise.resolve();
    },

    error: (
      message: string,
      context?: Record<string, unknown>,
      errorKind?: string,
      errorMessage?: string,
      stacktrace?: string,
    ): Promise<void> => {
      const datadogContext = toDatadogContext(context);
      if (shouldAttachError(errorKind, errorMessage, stacktrace)) {
        logger.error(
          message,
          datadogContext,
          createError(message, errorKind, errorMessage, stacktrace),
        );
      } else {
        logger.error(message, datadogContext);
      }
      return Promise.resolve();
    },
  };
}

/**
 * Minimal structural view of the Browser RUM API: `@datadog/browser-rum`'s `datadogRum`, or a
 * host page's CDN `window.DD_RUM`. Declared locally so this module stays `@datadog/*`-free.
 * Method syntax keeps `datadogRum` assignable despite its narrower `Context` parameter types.
 */
export type DatadogRumSink = {
  startView(options: { name: string; context: object }): void;
  addAction(name: string, context?: object): void;
  addError(error: Error, context?: object): void;
  addFeatureFlagEvaluation(key: string, value: string | number | boolean): void;
};

/** The RUM `IDatadogLogger` methods; pair with `createDatadogLogsMethods` for a full logger. */
type DatadogRumMethods = Pick<
  IDatadogLogger,
  "startView" | "stopView" | "addRumAction" | "addFeatureFlagEvaluation" | "addRumError"
>;

/**
 * The RUM methods for a web logger. `rum` is the injected Browser RUM API; `toDatadogContext`
 * applies the surface's own context enrichment.
 */
export function createDatadogRumMethods(
  rum: DatadogRumSink,
  toDatadogContext: (context?: Record<string, unknown>) => object,
): DatadogRumMethods {
  return {
    startView: (key: string, name?: string, context?: Record<string, unknown>): Promise<void> => {
      rum.startView({
        name: name ?? key,
        context: toDatadogContext({ ...context, viewKey: key }),
      });
      return Promise.resolve();
    },

    /**
     * Browser RUM ends the current view when the next view starts, so there is no web equivalent.
     */
    stopView: (): Promise<void> => Promise.resolve(),

    addRumAction: (name: string, context?: Record<string, unknown>): Promise<void> => {
      rum.addAction(name, toDatadogContext(context));
      return Promise.resolve();
    },

    addFeatureFlagEvaluation: (key: string, value: string | number | boolean): Promise<void> => {
      rum.addFeatureFlagEvaluation(key, value);
      return Promise.resolve();
    },

    addRumError: (
      message: string,
      source: string,
      stacktrace: string,
      context?: Record<string, unknown>,
    ): Promise<void> => {
      rum.addError(createError(message, source, message, stacktrace), toDatadogContext(context));
      return Promise.resolve();
    },
  };
}
